/**
 * The user-agent token the desktop shell appends to identify itself.
 *
 * Matched case-sensitively against a substring rather than parsed: Electron builds the UA from
 * `name/version`, so the shape around this token is not ours to depend on.
 */
export const SHELL_UA_TOKEN = "NihongonatorDesktop";

/**
 * Is this app running as its own window rather than as a tab in somebody's browser?
 *
 * **Framework-agnostic on purpose.** Two spellings, because the two things that can be true here
 * are answered by different mechanisms and neither covers the other: an installed PWA reports
 * `display-mode: standalone`, and the Electron shell — which loads this same remote origin in an
 * ordinary framed window — does not, so it announces itself in the user agent instead. Asking both
 * means this file does not have to know which one Alex is running, and does not need changing if
 * that answer changes.
 *
 * What it is *for* is the capture bridge's delivery rule. The tray agent sends a capture to a
 * standalone client in preference to a browser tab (`SseBroadcaster.DeliveryAudience`), which is
 * what stops a lingering tab and the shell both appending the same sentence.
 *
 * Both lookups are guarded. `matchMedia` is absent in the test environment and in older embedded
 * webviews, and `navigator` is absent under SSR — neither is a reason to throw on a boot path.
 */
export function isStandalone(nav: Navigator = navigator, win: Window = window): boolean {
  try {
    if (typeof win.matchMedia === "function" && win.matchMedia("(display-mode: standalone)").matches) {
      return true;
    }
  } catch {
    // A browser that dislikes the query is simply not a standalone one.
  }
  return typeof nav?.userAgent === "string" && nav.userAgent.includes(SHELL_UA_TOKEN);
}
